'use client';

import { safeWindow } from '@woonstad/helpers-utils';
import { useState } from 'react';

import { useEventListener } from './useEventListener';
import { useSessionStorage } from './useSessionStorage';

type ScrollPositionsType = { [key: string]: string };

export function useScrollRestoration() {
  const [scrollPositions, setScrollPositions] = useSessionStorage<ScrollPositionsType>('scrollPositions', {});
  const pathname = safeWindow.location?.pathname ?? '';

  const scrollPosition = Number(scrollPositions[pathname]);

  // Only attempt to restore if there is a valid stored position (> 0, not NaN).
  // When scrollPosition is NaN or 0, mark as already scrolled so we never call
  // scrollTo(NaN) which browsers interpret as scrollTo(0) — jumping to the top.
  const hasValidStoredPosition = scrollPosition > 0 && !isNaN(scrollPosition);
  const [hasScrolled, setHasScrolled] = useState(
    !hasValidStoredPosition || safeWindow.scrollY === scrollPosition,
  );

  for (const path in scrollPositions) {
    if (!pathname.includes(path)) {
      delete scrollPositions[path];
    }
  }

  const scrollTo = () => {
    if (typeof safeWindow.scrollTo === 'function') {
      safeWindow.scrollTo(0, scrollPosition);
      setHasScrolled(true);
    }
  };

  const handleScroll = () => {
    scrollPositions[pathname] = safeWindow.scrollY?.toString() ?? '0';
    setScrollPositions(scrollPositions);
  };

  useEventListener('scroll', handleScroll);

  if (!hasScrolled) {
    scrollTo();
  }
}
